import { useState, useEffect, useCallback } from 'react';
import { EditOutlined, FormOutlined } from '@ant-design/icons';
import { AModal, ASwitch, AInput } from '../../../components';
import { Col, Form, Row } from 'antd';
import { useToast } from '../../../hooks/toast';
import {
  schemaProcedureCreate,
  schemaProcedureEdit,
} from '../schemas/yup.procedure.schema';
import { IProcedure } from '../../../shared/interfaces';
import { useConfiguration } from '../../../hooks/configuration';
import { postProcedure, patchProcedure } from '../../../http';
import {
  formatToBRL,
  parseCurrencyInput,
} from '../../../shared/utils/functions';
import { useI18n } from '../../../shared/i18n';

interface IProcedureProps {
  open: boolean;
  close: () => void;
  procedure: IProcedure | null;
  setProcedures: React.Dispatch<React.SetStateAction<IProcedure[]>>;
}

const ProcedureCreateEdit: React.FC<IProcedureProps> = ({
  open,
  close,
  procedure,
  setProcedures,
}) => {
  const { configuration } = useConfiguration();
  const { addToast } = useToast();
  const { t } = useI18n();
  const [description, setDescription] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const [status, setStatus] = useState<boolean>(false);
  const [is_dental, setIs_dental] = useState<boolean>(false);
  const [is_recurrent, setIs_recurrent] = useState<boolean>(false);
  const [return_days, setReturn_days] = useState<number | ''>('');

  const handleCreate = useCallback(async () => {
    schemaProcedureCreate
      .validate({
        description,
        price,
        is_dental,
        is_recurrent,
        return_days: is_recurrent ? Number(return_days) : null,
      })
      .then(async (validated) => {
        const payload = { ...validated };
        if (!payload.is_recurrent) {
          (payload as any).return_days = null;
        }
        await postProcedure(payload)
          .then((data) => {
            setProcedures((state) => [...state, data]);
            addToast({
              type: 'success',
              title: t('proceduresPage.modal.toast.successTitle'),
              description: t('proceduresPage.modal.toast.createdDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('proceduresPage.modal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('proceduresPage.modal.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('proceduresPage.modal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [
    description,
    price,
    status,
    is_dental,
    is_recurrent,
    return_days,
    t,
    close,
    setProcedures,
    addToast,
  ]);

  const handleEdit = useCallback(async () => {
    schemaProcedureEdit
      .validate({
        description,
        status,
        price,
        is_dental,
        is_recurrent,
        return_days: is_recurrent ? Number(return_days) : null,
      })
      .then(async (validated) => {
        const payload = { ...validated };
        if (!payload.is_recurrent) {
          (payload as any).return_days = null;
        }
        await patchProcedure(procedure?.id, payload)
          .then((data) => {
            setProcedures((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: t('proceduresPage.modal.toast.infoTitle'),
              description: t('proceduresPage.modal.toast.updatedDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('proceduresPage.modal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('proceduresPage.modal.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('proceduresPage.modal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [
    description,
    price,
    status,
    is_dental,
    is_recurrent,
    return_days,
    t,
    close,
    setProcedures,
    addToast,
    procedure,
  ]);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = parseCurrencyInput(e.target.value);
    setPrice(numericValue);
  };

  useEffect(() => {
    if (procedure) {
      setDescription(procedure.description);
      setPrice(+procedure.price);
      setStatus(procedure.status);
      setIs_dental(procedure.is_dental ?? false);
      setIs_recurrent(procedure.is_recurrent ?? false);
      setReturn_days(
        procedure.is_recurrent && procedure.return_days != null
          ? procedure.return_days
          : '',
      );
    } else {
      setIs_recurrent(false);
      setReturn_days('');
    }
  }, [open]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        procedure ? (
          <>
            <EditOutlined /> {t('proceduresPage.modal.titleEdit')}
          </>
        ) : (
          <>
            <FormOutlined /> {t('proceduresPage.modal.titleCreate')}
          </>
        )
      }
      onOk={procedure ? handleEdit : handleCreate}
    >
      <Form layout="vertical" style={{ maxWidth: 600 }}>
        <Form.Item label={t('proceduresPage.modal.fields.description')}>
          <AInput
            placeholder={t(
              'proceduresPage.modal.fields.descriptionPlaceholder',
            )}
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Item>
        <Form.Item label={t('proceduresPage.modal.fields.price')}>
          <AInput
            placeholder={t('proceduresPage.modal.fields.pricePlaceholder')}
            name="price"
            value={formatToBRL(price)}
            onChange={handlePriceChange}
          />
        </Form.Item>
        <Row>
          {procedure && (
            <Col span={6}>
              <Form.Item label={t('proceduresPage.modal.fields.active')}>
                <ASwitch
                  checked={status}
                  checkedChildren={t('common.yes')}
                  unCheckedChildren={t('common.no')}
                  onChange={() => {
                    setStatus(!status);
                  }}
                />
              </Form.Item>
            </Col>
          )}
          {configuration?.dental && (
            <Col span={8}>
              <Form.Item label={t('proceduresPage.modal.fields.dental')}>
                <ASwitch
                  checked={is_dental}
                  checkedChildren={t('common.yes')}
                  unCheckedChildren={t('common.no')}
                  onChange={() => {
                    setIs_dental(!is_dental);
                  }}
                />
              </Form.Item>
            </Col>
          )}
          <Col span={8}>
            <Form.Item label="Procedimento Recorrente?">
              <ASwitch
                checked={is_recurrent}
                checkedChildren={t('common.yes')}
                unCheckedChildren={t('common.no')}
                onChange={() => {
                  setIs_recurrent(!is_recurrent);
                  if (is_recurrent) {
                    setReturn_days('');
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        {is_recurrent && (
          <Row>
            <Col span={8}>
              <Form.Item label="Dias para retorno">
                <AInput
                  type="number"
                  placeholder="Ex: 30"
                  min={1}
                  value={return_days}
                  onChange={(e) => {
                    const v = e.target.value;
                    setReturn_days(v === '' ? '' : Number(v));
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>
    </AModal>
  );
};

export default ProcedureCreateEdit;
